import { useEffect } from "react";
import { useGetChatFeatures } from "../api/useGetChatFeatures";
import { useSessionStorage } from "@mantine/hooks";
import { LocalStorageKeys } from "../enums/LocalStorageKeys";
import { ChatFeatures } from "../models/ChatFeatures";

const timeToLive =
  Number(import.meta.env.VITE_CHAT_FEATURES_TTL) || 1000 * 60 * 60; // 1 hour

export function useChatFeatures(isAuthenticated?: boolean) {
  const { features, getFeatures, isLoading } = useGetChatFeatures();
  const [savedFeatures, setSavedFeatures] = useSessionStorage<ChatFeatures>({
    key: LocalStorageKeys.ChatFeatures,
    defaultValue: undefined,
    // Need to set this to false to make sure it doesn't get the initial value undefined
    getInitialValueInEffect: false,
  });

  useEffect(() => {
    if (isAuthenticated === false) return;

    const lastRetrieved = savedFeatures?.lastRetrieved
      ? new Date(savedFeatures.lastRetrieved)
      : null;

    if (lastRetrieved && lastRetrieved.getTime() + timeToLive > Date.now()) {
      // If the last retrieved time is within the TTL, use the cached value
      return;
    }

    // Otherwise, fetch the features from the API
    getFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFeatures, isAuthenticated]);

  useEffect(() => {
    if (!features) return;
    setSavedFeatures(features);
  }, [features, setSavedFeatures]);

  return {
    features: savedFeatures,
    isLoading: !savedFeatures || isLoading,
  };
}
